import { z } from 'zod';
import { Id, IsoDateTime, JsonObject, Sha256Hex, TenantId } from './common.ts';
import { PlatformError } from './errors.ts';

/**
 * 17a (ADR-017): business operations. A pack owns the business identity (`namespace` + `key`, e.g.
 * `p2p.payment-instruction` + the payable allocation id) and the payload it hashes; core owns the atomic claim,
 * the links from every task attempt, the receipt and the reconciliation of uncertain outcomes. Action attempts
 * (ledger entries, `${taskId}:${actionId}` keys) stay separate: many attempts may link to one operation.
 * No promise of exactly-once external effects: an uncertain outcome is reconciled with the external system
 * before any reissue.
 */

/** Pack-owned namespace, dotted lowercase, e.g. `p2p.po-issue`. */
export const OperationNamespace = z
  .string()
  .regex(/^[a-z][a-z0-9-]*(\.[a-z0-9][a-z0-9-]*)+$/, 'namespace must be <pack>.<operation> lowercase')
  .meta({ id: 'OperationNamespace' });

export const BusinessOperationKey = z
  .object({
    namespace: OperationNamespace,
    /** Business identity inside the namespace, e.g. `PR-1001@rev3` or an allocation id. */
    key: z
      .string()
      .min(1)
      .max(255)
      .regex(/^[\x21-\x7e]+$/, 'printable ASCII without spaces'),
  })
  .meta({ id: 'BusinessOperationKey' });
export type BusinessOperationKey = z.infer<typeof BusinessOperationKey>;

/** `<namespace>:<key>`; unique per tenant. */
export function operationKey(op: BusinessOperationKey): string {
  return `${op.namespace}:${op.key}`;
}

/**
 * Who released an operation, why, and the evidence. Release is an explicit act: an operator with the pack's
 * release permission, or the pack's own compensation path (e.g. a bank return processed by the P2P pack). From
 * `succeeded`, `reversalRef` is the reversal evidence (bank return id, credit note); from `failed`, the failed
 * receipt id. Never released implicitly by a retry or a new proposal.
 */
export const OperationRelease = z
  .object({
    /** Authenticated principal (operator) or workload (pack compensation) that released it. */
    principalId: z.string().min(1),
    authority: z.enum(['operator', 'pack_compensation']),
    reason: z.string().min(1).max(2000),
    reversalRef: z.string().min(1),
    at: IsoDateTime,
  })
  .meta({ id: 'OperationRelease' });
export type OperationRelease = z.infer<typeof OperationRelease>;

export const BusinessOperationStatus = z
  .enum(['claimed', 'succeeded', 'failed', 'uncertain', 'released'])
  .meta({ id: 'BusinessOperationStatus' });
export type BusinessOperationStatus = z.infer<typeof BusinessOperationStatus>;

/**
 * Legal operation transitions.
 * - `claimed` → outcome of the in-flight attempt.
 * - `uncertain` → `succeeded | failed` only through reconciliation.
 * - `failed` → `claimed` (retry with the same payload) or `released`.
 * - `succeeded` → `released` only through an explicit, pack-defined reversal (e.g. a returned payment).
 * - `released` → `claimed` (reissue; the payload may differ).
 */
export const OPERATION_TRANSITIONS: Readonly<
  Record<BusinessOperationStatus, readonly BusinessOperationStatus[]>
> = {
  claimed: ['succeeded', 'failed', 'uncertain'],
  uncertain: ['succeeded', 'failed'],
  failed: ['claimed', 'released'],
  succeeded: ['released'],
  released: ['claimed'],
};

export function isLegalOperationTransition(
  from: BusinessOperationStatus,
  to: BusinessOperationStatus,
): boolean {
  return OPERATION_TRANSITIONS[from].includes(to);
}

/** One task attempt that tried (or reused) the operation. */
export const OperationAttempt = z
  .object({
    taskId: Id,
    actionId: Id,
    at: IsoDateTime,
    /** What the attempt got: the claim, or the prior result. */
    result: z.enum(['claimed', 'prior_result']).default('claimed'),
  })
  .meta({ id: 'OperationAttempt' });
export type OperationAttempt = z.infer<typeof OperationAttempt>;

export const BusinessOperation = z
  .object({
    operationId: Id,
    tenantId: TenantId,
    namespace: OperationNamespace,
    key: BusinessOperationKey.shape.key,
    /** `hashCanonical` of the pack-defined business payload (amount, beneficiary, lines …). */
    payloadHash: Sha256Hex,
    status: BusinessOperationStatus,
    attempts: z.array(OperationAttempt).default([]),
    /** Receipt of the attempt that settled the operation. */
    receiptRef: z.object({ actionId: Id, receiptId: Id }).optional(),
    externalOperationId: z.string().optional(),
    /** Result later attempts receive instead of executing again (e.g. the PO number). */
    result: JsonObject.optional(),
    releasedBy: OperationRelease.optional(),
    caseId: Id.optional(),
    /** Optimistic-concurrency version; every transition increments it. */
    version: z.number().int().nonnegative(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'BusinessOperation' });
export type BusinessOperation = z.infer<typeof BusinessOperation>;

export type ClaimDecision =
  | { kind: 'claim_new' }
  | { kind: 'reclaim'; from: 'failed' | 'released' }
  | { kind: 'prior_result'; operation: BusinessOperation };

/**
 * The claim rule every `OperationStore.claim` applies atomically (ADR-017):
 * - no operation → `claim_new`;
 * - `succeeded` with the same payload → `prior_result` (no second effect);
 * - `failed` with the same payload, or `released` with any payload → `reclaim`;
 * - `claimed` → `operation_conflict` (another attempt is in flight);
 * - `uncertain` → `operation_uncertain` (reconcile first);
 * - a different payload hash while not `released` → `operation_payload_mismatch`.
 */
export function decideClaim(existing: BusinessOperation | null, payloadHash: string): ClaimDecision {
  if (existing === null) return { kind: 'claim_new' };
  const details = {
    namespace: existing.namespace,
    key: existing.key,
    operationId: existing.operationId,
    status: existing.status,
  };
  if (existing.status === 'released') return { kind: 'reclaim', from: 'released' };
  if (existing.payloadHash !== payloadHash) {
    throw new PlatformError(
      'operation_payload_mismatch',
      'Business operation exists with a different payload',
      {
        details,
      },
    );
  }
  switch (existing.status) {
    case 'succeeded':
      return { kind: 'prior_result', operation: existing };
    case 'failed':
      return { kind: 'reclaim', from: 'failed' };
    case 'claimed':
      throw new PlatformError('operation_conflict', 'Business operation is claimed by another attempt', {
        details,
        retryable: true,
      });
    case 'uncertain':
      throw new PlatformError(
        'operation_uncertain',
        'Business operation outcome is uncertain; reconcile first',
        {
          details,
        },
      );
  }
}

// Declaring and deriving operations (ADR-017 §6) -----------------------------------------------

/** What an `ActionIntent` carries: the operation identity and payload hash, filled by pack code. */
export const BusinessOperationRef = BusinessOperationKey.extend({ payloadHash: Sha256Hex }).meta({
  id: 'BusinessOperationRef',
});
export type BusinessOperationRef = z.infer<typeof BusinessOperationRef>;

/**
 * Tool catalog declaration (`ToolCatalogEntry.operation`): executions of this tool are business operations in
 * `namespace`; the key is derived by the tool's handler-side deriver (`keyFrom: 'handler'`), never by the model.
 */
export const ToolOperationDeclaration = z
  .object({
    namespace: OperationNamespace,
    keyFrom: z.literal('handler'),
    /**
     * Added by 17b2: executions need a business gate. The gateway path denies an intent without
     * `subjectPreconditions` (`gate_required`), so a direct task cannot bypass the process's approval.
     */
    requiresGate: z.boolean().optional(),
  })
  .meta({ id: 'ToolOperationDeclaration' });
export type ToolOperationDeclaration = z.infer<typeof ToolOperationDeclaration>;

/**
 * Pack-side deriver. `authoritativeFacts` are read by pack code from the record that owns the obligation (e.g. the
 * payable allocation net of advances, credits, retention and prior settlements), not from model output; `args`
 * are the validated tool args and may only select which record to read.
 */
export type OperationKeyDeriver<Args = Record<string, unknown>, Facts = Record<string, unknown>> = (
  args: Args,
  authoritativeFacts: Facts,
) => BusinessOperationRef | Promise<BusinessOperationRef>;

/** Execution-attempt ids a business key must never embed (they change on every retry or regenerated proposal). */
export interface OperationKeyContext {
  taskId?: string;
  proposalId?: string;
  actionId?: string;
  runId?: string;
  episodeIds?: readonly string[];
  /** Namespace the tool declared; the derived namespace must equal it. */
  declaredNamespace?: string;
}

/**
 * Problems of a derived operation key (empty = valid): bad shape, a namespace other than the declared one, or a
 * key containing a task, proposal, action, run or episode id. Such a key would identify the attempt, not the
 * obligation, and let two attempts settle it twice.
 */
export function operationKeyIssues(op: BusinessOperationKey, context: OperationKeyContext = {}): string[] {
  const issues: string[] = [];
  const parsed = BusinessOperationKey.safeParse(op);
  if (!parsed.success) issues.push(...parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`));
  if (context.declaredNamespace !== undefined && op.namespace !== context.declaredNamespace) {
    issues.push(`namespace "${op.namespace}" differs from the declared "${context.declaredNamespace}"`);
  }
  const key = op.key.toLowerCase();
  const attemptIds: [string, string | undefined][] = [
    ['taskId', context.taskId],
    ['proposalId', context.proposalId],
    ['actionId', context.actionId],
    ['runId', context.runId],
    ...(context.episodeIds ?? []).map((id): [string, string] => ['episodeId', id]),
  ];
  for (const [name, id] of attemptIds) {
    if (id !== undefined && id.length > 0 && key.includes(id.toLowerCase())) {
      issues.push(`key contains the ${name} ${id}; derive it from the authoritative business record`);
    }
  }
  return issues;
}

export const OPERATION_EVENT_TYPES = { statusChanged: 'operation.status.changed' } as const;

/** `operation.status.changed`. */
export const OperationStatusChangedData = z
  .object({
    operationId: Id,
    namespace: OperationNamespace,
    key: z.string(),
    from: BusinessOperationStatus.optional(),
    to: BusinessOperationStatus,
    taskId: Id.optional(),
    actionId: Id.optional(),
    caseId: Id.optional(),
    at: IsoDateTime,
  })
  .meta({ id: 'OperationStatusChangedData' });
export type OperationStatusChangedData = z.infer<typeof OperationStatusChangedData>;
